import { CheckBoxWithLabelWidget, TextLinkKind, TextLinkWidget } from '@tableau/widgets';
import React from 'react';

import { ToasterService, ToastType } from '~commonInfra/services/ToasterService';
import { Messages } from '~src/codegen/Localize';
import { HelpService } from '~src/commonInfra/services/HelpService';
import WebHttpClient from '~src/httpClient/WebHttpClient';
import { learnMoreToken } from '~src/publishToSalesforce/components/PublishToSalesforceDialog';
import { getSessionManagementServiceInstance, SessionManagementService } from '~src/sessionManagement/SessionManagementService';
import { IGetSiteSettingsForSiteAdminResult } from '~src/settings/ServerApi/requests/siteSettings/GetSiteSettingsForSiteAdminRequest';
import { IUpdateSiteSettingsForSiteAdminParams, IUpdateSiteSettingsForSiteAdminResult, UpdateSiteSettingsForSiteAdminRequest } from '~src/settings/ServerApi/requests/siteSettings/UpdateSiteSettingsForSiteAdminRequest';
import { learnMoreStyle } from '~src/settings/styles/CommonStyles';
import { ISiteAdminSettings, ISiteSettingsForSiteAdmin } from '~src/settings/types/ISiteSettingsForSiteAdmin';
import { Sites } from '~src/siteManagement/services/resources/Sites';

import { integrationsSettingsPageHeader, settingsGroupContainer, settingsSubsection } from './styles/IntegrationsSettingsStyles';

export interface PublishToSalesforceSettingsState {
    readonly siteSettings: IGetSiteSettingsForSiteAdminResult;
    readonly isSiteSettingsUpdateBusy: boolean;
    readonly publishToSalesforceChecked: boolean;
    readonly hasError: boolean;
}

export interface PublishToSalesforceSettingsProps {
    readonly server: SessionManagementService;
    readonly sites: Sites;
}

export class PublishToSalesforceSettings extends React.Component<PublishToSalesforceSettingsProps, PublishToSalesforceSettingsState> {
    public constructor(props: PublishToSalesforceSettingsProps) {
        super(props);
        this.state = {
            siteSettings: undefined,
            hasError: false,
            publishToSalesforceChecked: false,
            isSiteSettingsUpdateBusy: false
        };
    }

    public async componentDidMount() {
        await this.getSiteSettings();
    }

    async getSiteSettings() {
        try {
            await this.props.server.getSessionInfoPromise();
            const siteSettings = await this.props.sites.getCurrentSiteSettingsForSiteAdmin();
            const siteAdminSettings: ISiteAdminSettings = siteSettings.siteAdminSettings as ISiteAdminSettings;
            this.setState({ siteSettings, publishToSalesforceChecked: siteAdminSettings.publishToSalesforceEnabled });
        } catch (e) {
            this.setState({ hasError: true });
        }
    }

    render(): JSX.Element {
        if (this.state.siteSettings && !this.state.hasError) {
            return (<div>
                {this.renderPublishToSalesforceHeader()}
                <div className={settingsGroupContainer}>
                    <div className={settingsSubsection}>
                        {this.renderPublishToSalesforceDescriptionAndLearnMoreLink()}
                        {this.renderPublishToSalesforceCheckbox()}
                    </div>
                </div>
            </div>
            );
        } else {
            return null;
        }
    }

    private updateSiteSettingsForSiteAdminPublishToSalesforce(checked: boolean) {
        this.setState({ publishToSalesforceChecked: checked, isSiteSettingsUpdateBusy: true });
        const siteSettingsForSiteAdmin: ISiteSettingsForSiteAdmin = this.state.siteSettings;
        const siteAdminSettings: ISiteAdminSettings = siteSettingsForSiteAdmin.siteAdminSettings as ISiteAdminSettings;
        siteAdminSettings.publishToSalesforceEnabled = checked;
        const params: IUpdateSiteSettingsForSiteAdminParams = {
            id: (siteSettingsForSiteAdmin as any).id,
            siteAdminSettings: siteAdminSettings,
            subscriptionSettings: siteSettingsForSiteAdmin.subscriptionSettings
        };
        try {
            WebHttpClient.sendRequest(new UpdateSiteSettingsForSiteAdminRequest(params)).then((result: IUpdateSiteSettingsForSiteAdminResult) => {
                if (result.errors) {
                    ToasterService.instance.pop({ type: ToastType.Error, text: Messages.publishToSalesforceSiteSettingUpdateFailure() });
                    this.setState({ publishToSalesforceChecked: !checked });
                } else {
                    this.forceUpdate();
                    getSessionManagementServiceInstance().refreshSessionInfo();
                }
                this.setState({ isSiteSettingsUpdateBusy: false });
            });
        } catch (e) {
            ToasterService.instance.pop({ type: ToastType.Error, text: Messages.publishToSalesforceSiteSettingUpdateFailure() });
            this.setState({ publishToSalesforceChecked: !checked, isSiteSettingsUpdateBusy: false });
        }
    }

    renderPublishToSalesforceHeader() {
        return <h2 className={integrationsSettingsPageHeader}>
            {Messages.publishToSalesforceSiteSettingHeader()}
        </h2>;
    }

    renderPublishToSalesforceDescriptionAndLearnMoreLink() {
        return (
            <div className={settingsSubsection}>
                <span>{Messages.publishToSalesforceSiteSettingExplanation()}</span>
                <TextLinkWidget
                    testId={`allow-publish-to-salesforce-learn-more`}
                    kind={TextLinkKind.Standard}
                    className={learnMoreStyle}
                    href={HelpService.getHelpUrlByCategory(learnMoreToken)}
                    target={'_blank'}
                    rel={'noreferrer noopener'}>
                    {Messages.publishToSalesforceDialogLearnMore()}
                </TextLinkWidget>
            </div>
        );
    }

    renderPublishToSalesforceCheckbox() {
        return (
            <CheckBoxWithLabelWidget
                label={Messages.publishToSalesforceSiteSettingExplanation()}
                checked={this.state.publishToSalesforceChecked}
                testId={'allow-publish-to-salesforce-checkbox'}
                removeLeftMargin={true}
                handleChange={this.updateSiteSettingsForSiteAdminPublishToSalesforce.bind(this)}
                disabled={this.state.isSiteSettingsUpdateBusy} />
        );
    }
}